import type { MappingLocation, SamsaraPlace } from '../../services/samsaraLocationMapping.service';

export type MatchStatus = 'strong' | 'review' | 'unmatched' | 'linked';
export interface Candidate { place: SamsaraPlace; distance: number | null; score: number; reasons: string[]; strong: boolean }
export interface LocationMatch { location: MappingLocation; status: MatchStatus; candidates: Candidate[] }
const words: Record<string, string> = { ROAD:'RD', DRIVE:'DR', STREET:'ST', BOULEVARD:'BLVD', HIGHWAY:'HWY', AVENUE:'AVE', NORTH:'N', SOUTH:'S', EAST:'E', WEST:'W', LANE:'LN', PARKWAY:'PKWY', TEXAS:'TX', GWII:'GWI', COURT:'CT', CIRCLE:'CIR', TERRACE:'TER', PLACE:'PL', SUITE:'STE', APARTMENT:'APT', BUILDING:'BLDG' };
const ignored = new Set(['THE','INC','LLC','CO','CORPORATION','COMPANY','WAREHOUSE','TERMINAL','LOCATION','YARD','LOGISTICS']);
const normalize = (value?: string | null) => (value || '').replace(/#/g, ' UNIT ').toUpperCase().match(/[A-Z0-9]+/g)?.map(w=>words[w]||w).join(' ') || '';
const tokens = (value: string) => new Set(normalize(value).split(' ').filter(w=>w&&!ignored.has(w)));
const overlap = (a: Set<string>, b: Set<string>) => [...a].filter(w=>b.has(w)).length / Math.max(1,new Set([...a,...b]).size);
function bigrams(value: string) { return new Set(Array.from({length:Math.max(0,value.length-1)},(_,i)=>value.slice(i,i+2))); }
function center(value?: string): [number,number] | null {
  const points=(value||'').split(';').filter(p=>p.trim()).map(p=>p.split(',').map(Number));
  if (!points.length||points.some(p=>p.length!==2||p.some(v=>!Number.isFinite(v))||Math.abs(p[0])>90||Math.abs(p[1])>180||(p[0]===0&&p[1]===0))) return null;
  return [points.reduce((sum,p)=>sum+p[0],0)/points.length,points.reduce((sum,p)=>sum+p[1],0)/points.length];
}
function miles(a: [number,number] | null, p: SamsaraPlace): number | null {
  if (!a||p.latitude==null||p.longitude==null||!Number.isFinite(p.latitude)||!Number.isFinite(p.longitude)) return null;
  const rad=Math.PI/180, dlat=(p.latitude-a[0])*rad, dlng=(p.longitude-a[1])*rad;
  return 7917.5226*Math.asin(Math.sqrt(Math.min(1,Math.sin(dlat/2)**2+Math.cos(a[0]*rad)*Math.cos(p.latitude*rad)*Math.sin(dlng/2)**2)));
}
export const locationAddress = (l: MappingLocation) => [l.ladd,l.ladd2,l.lcity,l.lstate,l.lzip].filter(Boolean).join(', ');

const unitPattern = /\b(?:STE|UNIT|APT|BLDG)\s+([A-Z0-9]+)\b/;
const withoutUnit = (address: string) => address.replace(unitPattern, '').trim();
const houseNumber = (address: string) => address.match(/^\d+[A-Z]?\b/)?.[0];

/** Street comparison only; the UI separately shows city, ZIP and review reasons. */
export function streetAddressesMatch(location: MappingLocation, place?: SamsaraPlace): boolean {
  if (!place) return false;
  const street = withoutUnit(normalize(location.ladd));
  const address = normalize(place.address);
  return !!street && (place.address.includes(',')
    ? withoutUnit(normalize(place.address.split(',')[0])) === street
    : address === street || address.startsWith(street + ' '));
}

/** Address evidence determines eligibility; names only help rank address matches. */
export async function matchLocations(locations: MappingLocation[], places: SamsaraPlace[], signal?: AbortSignal): Promise<LocationMatch[]> {
  const prepared = places.map(place => {
    const address = normalize(place.address);
    const region = address.match(/\b([A-Z]{2}) (\d{5})(?: \d{4})?(?: (?:USA|US|UNITED STATES))?$/);
    return {
      place, address, street: withoutUnit(normalize(place.address.split(',')[0])),
      hasCommas: place.address.includes(','), number: houseNumber(address),
      unit: address.match(unitPattern)?.[1],
      zip: region?.[2] || address.match(/\b(\d{5})(?: \d{4})?(?: (?:USA|US|UNITED STATES))?$/)?.[1],
      // A street-only value may end in ST/RD/CT; that is not a region.
      state: region?.[1] || (place.address.includes(',')
        ? normalize(place.address.slice(place.address.indexOf(',') + 1)).match(/\b([A-Z]{2})(?: (?:USA|US|UNITED STATES))?$/)?.[1]
        : undefined),
      name: normalize(place.name), nameTokens: tokens(place.name), nameGrams: bigrams(normalize(place.name)),
    };
  });
  const byNumber = new Map<string, number[]>();
  prepared.forEach((p, i) => {
    if (p.number) byNumber.set(p.number, [...(byNumber.get(p.number) || []), i]);
  });
  const ids = new Map(places.map((p, i) => [p.id, i]));
  const rows: LocationMatch[] = [];
  for (let k = 0; k < locations.length; k++) {
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    if (k % 40 === 0) await new Promise(resolve => setTimeout(resolve, 0));
    const location = locations[k];
    if (location.deleted === 'Y') continue;
    const name = normalize(location.lname), nt = tokens(name), ng = bigrams(name);
    const street = withoutUnit(normalize(location.ladd)), number = houseNumber(street);
    const unit = normalize([location.ladd, location.ladd2].filter(Boolean).join(' ')).match(unitPattern)?.[1];
    const zip = location.lzip?.trim().slice(0, 5), city = normalize(location.lcity), state = normalize(location.lstate);
    const point = center(location.coordinates), linked = location.samsara_loc_id?.trim();
    const possible = new Set(number ? byNumber.get(number) : []);
    if (linked && ids.has(linked)) possible.add(ids.get(linked)!);
    const candidates: Candidate[] = [];
    const exactAddresses = new Set<string>();
    possible.forEach(i => {
      const p = prepared[i], d = miles(point, p.place), isLinked = p.place.id === linked;
      const sameNumber = !!number && p.number === number;
      const sameStreet = streetAddressesMatch(location, p.place);
      const sameZip = !!zip && p.zip === zip;
      const sameCityState = !!city && !!state && (` ${p.address} `).includes(` ${city} ${state} `);
      const stateConflict = !!state && !!p.state && state !== p.state;
      const zipConflict = !!zip && !!p.zip && zip !== p.zip;
      const unitConflict = !!unit && !!p.unit && unit !== p.unit;
      const unitIncomplete = !!unit !== !!p.unit;
      const nearby = d !== null && d <= 1;
      const comparableStreet = p.hasCommas ? p.street : withoutUnit(p.address.split(' ').slice(0, street.split(' ').length).join(' '));
      const addressSimilarity = overlap(bigrams(street), bigrams(comparableStreet));
      // Never suggest from name or GPS alone, or override explicit address conflicts.
      if (!isLinked && (!sameNumber || stateConflict || unitConflict
          || (!sameStreet && addressSimilarity < .72)
          || !(sameZip || sameCityState || (nearby && !zipConflict)))) return;
      const ns = Math.max(overlap(nt, p.nameTokens), overlap(ng, p.nameGrams));
      const proximity = d === null ? 0 : Math.max(0, 1 - d / 2);
      // Exact addresses always outrank fuzzy ones; name and GPS are small tie-breakers.
      let score = (sameStreet ? 80 : 50 * addressSimilarity) + (sameZip || sameCityState ? 10 : 0) + 4 * ns + 3 * proximity;
      let strong = sameStreet && sameNumber && (sameZip || sameCityState)
        && !zipConflict && !unitIncomplete && (d === null || d <= 1);
      const reasons: string[] = [];
      if (sameStreet) reasons.push('Same normalized street');
      else if (sameNumber) reasons.push('Similar street, same number');
      if (sameZip) reasons.push('Same ZIP');
      if (sameCityState) reasons.push('Same city / state');
      if (zipConflict) reasons.push('Different ZIP — verify address');
      if (unitIncomplete) reasons.push('Suite / unit missing on one address');
      if (unit && p.unit === unit) reasons.push('Same suite / unit');
      if (ns >= .85) reasons.push('Very similar name');
      else if (ns >= .55) reasons.push('Similar name');
      if (d !== null) reasons.push(`${d.toFixed(2)} mi between centers`);
      if (d !== null && d > 5) { score -= 5; reasons.push('Coordinates disagree — verify address'); }
      const a = name.match(/\d+/g), b = p.name.match(/\d+/g);
      if (a && b && [...new Set(a)].sort().join(',') !== [...new Set(b)].sort().join(',')) {
        strong = false; reasons.push('Different facility numbers');
      }
      if (/CHECK IN|ENTRANCE|CONGESTION/.test(p.name)) {
        strong = false; reasons.push('Verify entrance / geofence purpose');
      }
      candidates.push({ place: p.place, distance: d, score, strong, reasons });
      if (sameStreet) exactAddresses.add(p.place.id);
    });
    candidates.sort((a, b) => Number(b.place.id === linked) - Number(a.place.id === linked) || b.score - a.score);
    const top = candidates[0], second = candidates[1];
    // Same-address alternatives remain ambiguous even when one name is closer.
    const ambiguous = top && second && (top.score - second.score < 8
      || (exactAddresses.has(top.place.id) && exactAddresses.has(second.place.id)));
    if (ambiguous) top.reasons.push('Multiple plausible address matches');
    rows.push({ location, candidates: candidates.slice(0, 3), status: linked ? 'linked' : !top ? 'unmatched' : top.strong && !ambiguous ? 'strong' : 'review' });
  }
  const uses = new Map<string, number>();
  rows.forEach(r => { const id = r.candidates[0]?.place.id; if (id) uses.set(id, (uses.get(id) || 0) + 1); });
  rows.forEach(r => {
    if ((uses.get(r.candidates[0]?.place.id) || 0) > 1) {
      r.candidates[0].reasons.push('Also suggested for another CMS location');
      if (r.status === 'strong') r.status = 'review';
    }
  });
  return rows;
}

/** Import is preview-only. Saves always validate against the server's live catalog. */
export function parsePlaceExport(input: unknown): SamsaraPlace[] {
  const root=input as {data?:unknown;pagination?:{hasNextPage?:boolean}};
  if(root?.pagination?.hasNextPage)throw new Error('This is a partial Samsara page. Import the complete export.');
  const rows=Array.isArray(input)?input:root?.data;
  if(!Array.isArray(rows)||!rows.length||rows.length>25000)throw new Error('Import a JSON array of Samsara places (maximum 25,000).');
  const ids=new Set<string>();
  return rows.map(row=>{
    if(!row||typeof row!=='object')throw new Error('Invalid Samsara place.');
    const id=String(row.id||'');
    if(!/^[0-9]{1,64}$/.test(id)||!row.name)throw new Error('Each place needs a numeric ID and a name.');
    if(ids.has(id))throw new Error('Duplicate Samsara place IDs in export.');ids.add(id);
    return {id,name:String(row.name),address:String(row.address||row.formattedAddress||''),latitude:typeof row.latitude==='number'?row.latitude:null,longitude:typeof row.longitude==='number'?row.longitude:null};
  });
}
